import API16 from '@carbon/icons-react/es/api/16';
import Box16 from '@carbon/icons-react/es/box/16';
import Collaborate16 from '@carbon/icons-react/es/collaborate/16';
import ColorPalette16 from '@carbon/icons-react/es/color-palette/16';
import Email16 from '@carbon/icons-react/es/email/16';
import Event16 from '@carbon/icons-react/es/event/16';
import EventsAlt16 from '@carbon/icons-react/es/events--alt/16';
import Hourglass16 from '@carbon/icons-react/es/hourglass/16';
import Identification16 from '@carbon/icons-react/es/identification/16';
import Integration16 from '@carbon/icons-react/es/integration/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Money16 from '@carbon/icons-react/es/money/16';
import Notification16 from '@carbon/icons-react/es/notification/16';
import Partnership16 from '@carbon/icons-react/es/partnership/16';
import PiggyBank16 from '@carbon/icons-react/es/piggy-bank/16';
import Security16 from '@carbon/icons-react/es/security/16';
import TagGroup16 from '@carbon/icons-react/es/tag--group/16';
import Task16 from '@carbon/icons-react/es/task/16';
import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import UserData16 from '@carbon/icons-react/es/user--data/16';
import UserMultiple16 from '@carbon/icons-react/es/user--multiple/16';
import Wikis16 from '@carbon/icons-react/es/wikis/16';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { PermissionSetType } from 'api/models';
import { ReactComponent as Advance } from 'assets/svgs/advance.svg';
import { BillingSettings } from 'components/organisms/module-settings/billing';
import { CustomDomain } from 'components/organisms/module-settings/custom-domain';
import { CustomerNotificationSettingsPage } from 'components/organisms/module-settings/customer-portal-config';
import { EmailConfiguration } from 'components/organisms/module-settings/email-configuration';
import { Financials } from 'components/organisms/module-settings/financials/financials';
import { Integrations } from 'components/organisms/module-settings/integrations';
import {
  Workato,
  Zapier,
} from 'components/organisms/module-settings/integrations/integrations-list';
import { Jira } from 'components/organisms/module-settings/integrations/integrations-list/jira/jira';
import { KeyeventsAndIntervalsSettings } from 'components/organisms/module-settings/key-events-and-intervals/settings';
import { NotificationSettingsPage } from 'components/organisms/module-settings/notifications';
import { ProfileSettingsPage } from 'components/organisms/module-settings/profile';
import { ResourceManagementSettings } from 'components/organisms/module-settings/resource-management';
import { TeamMemberSettingsPage } from 'components/organisms/module-settings/team-members/team-members';
import { AdvancedSettingsPage } from 'components/pages';
import { PartnerList } from 'components/pages/partner';
import { RocketlaneAPIPage } from 'components/pages/rocketlane-api';
import { TimesheetSettings } from 'components/pages/timesheet-settings';
import { Krypton } from 'components/templates/krypton';
import { PermissionRuleName, RoutePaths } from 'constant';
import { useUserPermissionSetType } from 'hooks/permissions';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useBillingFeatureFlag, useIsAccountActive } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useHasAccess } from 'hooks/use-has-access';
import { useTranslation } from 'hooks/use-translation';
import { SecureRoute } from 'routes/route-with-permission-check';
import { NotFound } from '../404';
import { ArchivedProjectsPage } from './archived-projects';
import { HubspotIntegrationsLandingPage } from './integrations/hubspot';
import { SalesForceIntegrationsLandingPage } from './integrations/salesforce/salesforce-page';
import { SlackLandingPage } from './integrations/slack/slack-landing-page';
import { OrganisationSettingPage } from './organisation';
import { PartnerRoute } from './partner';
import { ProjectFieldsV2SettingsPage } from './project-fields-v2';
import { RateCardsPage } from './rate-cards';
import { RolesSettingsPage } from './roles';
import { SecurityPage } from './security';
import { ArchiveButton, StyledLink } from './styled';
import { TaskFieldsSettingsPage } from './task-fields';
import { TeamMemberFieldsSettingsPage } from './team-member-fields';

export const MenuSpan = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const publicAPIAcceptedDomains = [
  'uniphore.rocketlane.com',
  'uniphore-sandbox.rocketlane.com',
];

export const AllSettings = () => {
  const location = useLocation();
  const title = useTranslation('settings');
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isSuperUser =
    currentUserPermissionSetType === PermissionSetType.SuperUser;
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const [accountability] = useAccountFeature(AccountFeature.Accountability);
  const { hasAccess: canReadRateCards } = useHasAccess({
    permissions: ['READ'],
    resource: 'rateCard',
  });
  const canViewRateCards =
    accountability && isFinancialsEnabled && canReadRateCards;
  const canAccessFinancials = accountability && isAccountAdmin;
  const canAccessBilling = useBillingFeatureFlag();
  const canAccessKeyeventsAndIntervals = isAccountAdmin;

  const isAccActive = useIsAccountActive();
  const navLinkObj = useMemo(
    () =>
      !isAccActive
        ? isAccountAdmin
          ? [
              {
                value: [
                  {
                    menuName: <FormattedMessage id="settings.billing.title" />,
                    menuIcon: <Money16 />,
                    route: RoutePaths.billingSettings,
                    ruleName: PermissionRuleName.BillingPage,
                  },
                  {
                    menuName: <FormattedMessage id="team_members" />,
                    menuIcon: <UserMultiple16 />,
                    route: RoutePaths.teamMemberSettings,
                    ruleName: PermissionRuleName.TeamMemberSettingPage,
                  },
                ],
              },
            ]
          : []
        : [
            {
              subHead: <FormattedMessage id="settings.side_nav.personal" />,
              value: [
                {
                  menuName: <FormattedMessage id="profile" />,
                  menuIcon: <UserAvatar16 />,
                  route: RoutePaths.profile,
                },
                {
                  menuName: <FormattedMessage id="notifications" />,
                  menuIcon: <Notification16 />,
                  route: RoutePaths.notificationsSettings,
                },
              ],
            },
            {
              subHead: <FormattedMessage id="settings.side_nav.account" />,
              value: [
                {
                  menuName: <FormattedMessage id="preferences" />,
                  menuIcon: <ColorPalette16 />,
                  route: RoutePaths.organisationSettings,
                  ruleName: PermissionRuleName.OrganisationSettingsPage,
                },
                ...(canAccessBilling
                  ? [
                      {
                        menuName: (
                          <FormattedMessage id="settings.billing.title" />
                        ),
                        menuIcon: <Money16 />,
                        route: RoutePaths.billingSettings,
                        ruleName: PermissionRuleName.BillingPage,
                      },
                    ]
                  : []),
                {
                  menuName: <FormattedMessage id="team_members" />,
                  menuIcon: <UserMultiple16 />,
                  route: RoutePaths.teamMemberSettings,
                  ruleName: PermissionRuleName.TeamMemberSettingPage,
                },
                {
                  menuName: <FormattedMessage id="settings.roles.menu.title" />,
                  menuIcon: <Identification16 />,
                  route: RoutePaths.rolesSettings,
                  ruleName: PermissionRuleName.RoleSettingPage,
                },
                {
                  menuName: <FormattedMessage id="settings.custom_portal" />,
                  menuIcon: <Collaborate16 />,
                  route: `${RoutePaths.customerPortalNotificationSettings}/${
                    location.pathname.includes('/client-notification')
                      ? 'client-notification'
                      : 'project-tabs'
                  }`,
                  ruleName: PermissionRuleName.CustomerPortalSettingPage,
                },
                {
                  menuName: <FormattedMessage id="partners" />,
                  menuIcon: <Partnership16 />,
                  route: RoutePaths.partner,
                  ruleName: PermissionRuleName.PartnerSettingPage,
                },
                {
                  menuName: <FormattedMessage id="integrations" />,
                  menuIcon: <Integration16 />,
                  route: RoutePaths.integrations,
                  ruleName: PermissionRuleName.IntegrationSettingPage,
                },
              ],
            },
            {
              subHead: (
                <FormattedMessage id="settings.side_nav.op_financials" />
              ),
              value: [
                {
                  menuName: <FormattedMessage id="settings.timesheets.title" />,
                  menuIcon: <Hourglass16 />,
                  route: RoutePaths.timesheetSettings,
                  ruleName: PermissionRuleName.TimesheetSettingPage,
                },

                {
                  menuName: (
                    <FormattedMessage id="settings.resource_management.title" />
                  ),
                  menuIcon: <EventsAlt16 />,
                  route: RoutePaths.resourceManagementSettings,
                  ruleName: PermissionRuleName.ResourceManagementSettingPage,
                },

                ...(canAccessFinancials
                  ? [
                      {
                        menuName: (
                          <FormattedMessage id="settings.project_account.title" />
                        ),
                        menuIcon: <PiggyBank16 />,
                        route: RoutePaths.financials,
                      },
                    ]
                  : []),
                ...(canViewRateCards
                  ? [
                      {
                        menuName: (
                          <FormattedMessage id="settings.rate_cards.title" />
                        ),
                        menuIcon: <TagGroup16 />,
                        route: RoutePaths.rateCards,
                      },
                    ]
                  : []),
              ],
            },
            {
              subHead: <FormattedMessage id="settings.side_nav.fields" />,
              value: [
                {
                  menuName: <FormattedMessage id="project_fields" />,
                  menuIcon: <Task16 />,
                  route: RoutePaths.projectFields,
                  ruleName: PermissionRuleName.ProjectFieldSettingPage,
                },
                {
                  menuName: <FormattedMessage id="task_fields" />,
                  menuIcon: <ListChecked16 />,
                  route: RoutePaths.taskFields,
                  ruleName: PermissionRuleName.TaskFieldSettingPage,
                },
                {
                  menuName: <FormattedMessage id="team_member_fields" />,
                  menuIcon: <UserData16 />,
                  route: RoutePaths.teamMemberFields,
                  ruleName: PermissionRuleName.TeamMemberFieldSettingPage,
                },
              ],
            },
            {
              subHead: <FormattedMessage id="settings.side_nav.advanced" />,
              value: [
                {
                  menuName: <FormattedMessage id="custom_domain" />,
                  menuIcon: <Wikis16 />,
                  route: RoutePaths.customDomain,
                  ruleName: PermissionRuleName.CustomDomainSettingPage,
                },

                {
                  menuName: <FormattedMessage id="email_configuration" />,
                  menuIcon: <Email16 />,
                  route: RoutePaths.emailConfiguration,
                  ruleName: PermissionRuleName.EmailConfigurationSettingPage,
                },
                {
                  menuName: <FormattedMessage id="security" />,
                  menuIcon: <Security16 />,
                  route: RoutePaths.security,
                  ruleName: PermissionRuleName.SecuritySettingPage,
                },
                ...(publicAPIAcceptedDomains.includes(
                  window.location.hostname,
                ) && isAccountAdmin
                  ? [
                      {
                        menuName: <FormattedMessage id="settings.api" />,
                        menuIcon: <API16 />,
                        route: RoutePaths.api,
                      },
                    ]
                  : []),
                ...(canAccessKeyeventsAndIntervals
                  ? [
                      {
                        menuName: (
                          <FormattedMessage id="settings.keyevents_and_intervals.title" />
                        ),
                        menuIcon: <Event16 />,
                        route: RoutePaths.keyeventsAndIntervals,
                      },
                    ]
                  : []),

                {
                  menuName: (
                    <FormattedMessage id="settings.advanced.workflow.management" />
                  ),
                  menuIcon: <Advance />,
                  route: RoutePaths.advanced,
                  ruleName: PermissionRuleName.AdvancedSettingPage,
                },
              ],
            },
          ],
    [
      canAccessBilling,
      canAccessKeyeventsAndIntervals,
      canAccessFinancials,
      canViewRateCards,
      isAccActive,
      isAccountAdmin,
      location.pathname,
    ],
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const accessibleNavItems = useMemo(
    () =>
      navLinkObj
        .map((item) => ({
          ...item,
          value: item.value.filter(
            ({ ruleName }) =>
              ruleName == null || isAllowedForCurrentUser(ruleName),
          ),
        }))
        .filter(({ value }) => value.length > 0),
    [isAllowedForCurrentUser, navLinkObj],
  );

  return (
    <>
      <Krypton
        documentTitle={title}
        title={title}
        leftMenuItems={accessibleNavItems}
        footer={
          isAccActive && (isAccountAdmin || isSuperUser) ? (
            <StyledLink to={RoutePaths.archivedProjects}>
              <ArchiveButton
                data-test-id="project.archived_projects.page.link"
                iconPlacement="left"
                icon={<Box16 />}
                type="link"
              >
                <FormattedMessage id="project.archived_projects.title" />
              </ArchiveButton>
            </StyledLink>
          ) : null
        }
      >
        <Switch>
          <SecureRoute
            path={RoutePaths.profile}
            component={ProfileSettingsPage}
          />
          <SecureRoute
            path={RoutePaths.organisationSettings}
            component={OrganisationSettingPage}
            ruleName={PermissionRuleName.OrganisationSettingsPage}
          />
          <SecureRoute
            path={RoutePaths.customDomain}
            component={CustomDomain}
            ruleName={PermissionRuleName.CustomDomainSettingPage}
          />
          <SecureRoute
            path={RoutePaths.emailConfiguration}
            component={EmailConfiguration}
            ruleName={PermissionRuleName.EmailConfigurationSettingPage}
          />
          <SecureRoute
            path={RoutePaths.integrations}
            component={Integrations}
            exact
            ruleName={PermissionRuleName.IntegrationSettingPage}
          />
          <SecureRoute
            path="/settings/notifications"
            component={NotificationSettingsPage}
          />
          <SecureRoute
            path={RoutePaths.customerPortalNotificationSettings}
            component={CustomerNotificationSettingsPage}
            ruleName={PermissionRuleName.CustomerPortalSettingPage}
          />
          <SecureRoute
            path="/settings/team-members"
            component={TeamMemberSettingsPage}
            ruleName={PermissionRuleName.TeamMemberSettingPage}
          />
          <SecureRoute
            path={RoutePaths.timesheetSettings}
            component={TimesheetSettings}
            ruleName={PermissionRuleName.TimesheetSettingPage}
          />
          <SecureRoute
            path={RoutePaths.projectFields}
            component={ProjectFieldsV2SettingsPage}
            ruleName={PermissionRuleName.ProjectFieldSettingPage}
          />
          <SecureRoute
            path={RoutePaths.taskFields}
            component={TaskFieldsSettingsPage}
            ruleName={PermissionRuleName.TaskFieldSettingPage}
          />
          <SecureRoute
            path={RoutePaths.teamMemberFields}
            component={TeamMemberFieldsSettingsPage}
            ruleName={PermissionRuleName.TeamMemberFieldSettingPage}
          />
          <SecureRoute
            path={RoutePaths.rolesSettings}
            component={RolesSettingsPage}
            ruleName={PermissionRuleName.RoleSettingPage}
          />
          <SecureRoute
            path={RoutePaths.resourceManagementSettings}
            component={ResourceManagementSettings}
            ruleName={PermissionRuleName.ResourceManagementSettingPage}
          />
          <SecureRoute path="/settings" exact>
            <Redirect to={RoutePaths.profile} />
          </SecureRoute>
          <SecureRoute
            path={RoutePaths.archivedProjects}
            component={ArchivedProjectsPage}
          />
          <SecureRoute
            path={RoutePaths.advanced}
            component={AdvancedSettingsPage}
            ruleName={PermissionRuleName.AdvancedSettingPage}
          />
          {publicAPIAcceptedDomains.includes(window.location.hostname) &&
            isAccountAdmin && (
              <SecureRoute
                path={RoutePaths.api}
                component={RocketlaneAPIPage}
              />
            )}
          {canAccessBilling && (
            <SecureRoute
              path={RoutePaths.billingSettings}
              component={BillingSettings}
              ruleName={PermissionRuleName.BillingPage}
            />
          )}
          {canAccessKeyeventsAndIntervals && (
            <SecureRoute
              path={RoutePaths.keyeventsAndIntervals}
              component={KeyeventsAndIntervalsSettings}
              ruleName={PermissionRuleName.ProjectFieldSettingPage}
            />
          )}
          <SecureRoute
            path={RoutePaths.security}
            component={SecurityPage}
            ruleName={PermissionRuleName.SecuritySettingPage}
          />
          <SecureRoute
            path={RoutePaths.partner}
            component={PartnerList}
            ruleName={PermissionRuleName.PartnerSettingPage}
          />
          {canViewRateCards ? (
            <SecureRoute
              path={RoutePaths.rateCards}
              component={RateCardsPage}
            />
          ) : null}
          {canAccessFinancials ? (
            <SecureRoute path={RoutePaths.financials} component={Financials} />
          ) : null}
          <SecureRoute path="*">
            <NotFound />
          </SecureRoute>
        </Switch>
      </Krypton>
    </>
  );
};

export const SettingsRoute: FC = () => {
  const { path } = useRouteMatch();

  return (
    <>
      {/* These are listed seperately as the layout of these is different than settings page */}
      <Switch>
        <SecureRoute
          path={RoutePaths.jiraIntegration}
          component={Jira}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.zapierIntegration}
          component={Zapier}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.workatoIntegration}
          component={Workato}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.salesforceIntegrations}
          component={SalesForceIntegrationsLandingPage}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.slackIntegration}
          component={SlackLandingPage}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.hubspotIntegration}
          component={HubspotIntegrationsLandingPage}
          ruleName={PermissionRuleName.IntegrationSettingPage}
        />
        <SecureRoute
          path={RoutePaths.partner}
          component={PartnerRoute}
          ruleName={PermissionRuleName.PartnerSettingPage}
        />
        <SecureRoute path={path} component={AllSettings} />
      </Switch>
    </>
  );
};
